// @ts-nocheck
// Ported byte-identically from v1 src/service/webrtcCallStats.js.
// Zero React / DOM chrome — pure utility that polls an RTCPeerConnection's
// getStats() and derives per-tick media quality (MOS via a simplified G.107
// E-model, RTT/jitter/loss, mic drops, echo, TURN relay, codec details) plus
// a rolling accumulator + downsampled timeline handed to the backend on stop.
// Constants, thresholds, formulae, warning codes, streak counters, and public
// API (startWebrtcStatsMonitor + QUALITY_THRESHOLDS + mosToLabel/mosToBars +
// estimateMos) are unchanged.
const DEFAULT_INTERVAL_MS = 2000;
// ~30 min of samples at 2s. Ring buffer so a long call can't grow unbounded.
const MAX_SAMPLES = 900;
// Points kept in the timeline handed to the backend (downsampled on export).
const MAX_TIMELINE_POINTS = 180;

// Thresholds tuned for narrowband/wideband VoIP (PCMU / G.722 / Opus).
export const QUALITY_THRESHOLDS = {
  lossWarn: 3, // %
  lossBad: 8,
  jitterWarn: 30, // ms
  jitterBad: 60,
  rttWarn: 250, // ms
  rttBad: 500,
  concealmentWarn: 5, // %
  concealmentBad: 12,
  // audioLevel is 0..1; anything below this for silentSamplesForAlert samples
  // in a row means "no speech energy at all on this leg".
  silenceLevel: 0.0005,
  silentSamplesForAlert: 5,
  // Echo return loss in dB. Higher = better cancellation. Below ~20dB the far
  // end starts hearing themselves.
  erlWarn: 20,
  erlBad: 12,
  // Uplink queueing: mean per-packet send delay in ms.
  sendDelayWarn: 40,
  sendDelayBad: 120,
  // Mic capture drops per sample window (seconds of audio lost at the mic).
  captureDropWarn: 0.02,
  // Jitter buffer stretch/compress as % of received samples -> clock drift.
  bufferAdjustWarn: 2,
  // Codec clock rate below this = narrowband (no HD voice).
  wideBandClockRate: 16000,
};

const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const clamp = (v, min, max) => Math.min(max, Math.max(min, v));
const round = (v, d = 2) =>
  v === null || v === undefined || Number.isNaN(v)
    ? null
    : Math.round(v * 10 ** d) / 10 ** d;

const safeDelta = (curr, prev) => {
  const c = num(curr);
  const p = num(prev);
  if (c === null) return null;
  if (p === null) return null;
  const d = c - p;
  // Counters only ever grow; a negative delta means the stats object was
  // replaced (ICE restart / re-INVITE) — treat as "no reading this tick".
  return d < 0 ? null : d;
};

/**
 * Simplified ITU-T G.107 E-model. Same formula family used by most VoIP
 * dashboards: effective latency -> R factor -> MOS.
 */
export const estimateMos = ({ rttMs, jitterMs, lossPercent }) => {
  const rtt = num(rttMs) ?? 0;
  const jitter = num(jitterMs) ?? 0;
  const loss = num(lossPercent) ?? 0;

  const effectiveLatency = rtt + jitter * 2 + 10;
  let r =
    effectiveLatency < 160
      ? 93.2 - effectiveLatency / 40
      : 93.2 - (effectiveLatency - 120) / 10;
  r -= loss * 2.5;
  r = clamp(r, 0, 93.2);

  const mos = 1 + 0.035 * r + 0.000007 * r * (r - 60) * (100 - r);
  return Math.round(clamp(mos, 1, 4.5) * 100) / 100;
};

export const mosToLabel = (mos) => {
  const m = num(mos);
  if (m === null) return 'unknown';
  if (m >= 4.2) return 'excellent';
  if (m >= 3.8) return 'good';
  if (m >= 3.1) return 'fair';
  if (m >= 2.4) return 'poor';
  return 'bad';
};

/** 0-5 bar count for the UI indicator. */
export const mosToBars = (mos) => {
  const m = num(mos);
  if (m === null) return 0;
  if (m >= 4.2) return 5;
  if (m >= 3.8) return 4;
  if (m >= 3.1) return 3;
  if (m >= 2.4) return 2;
  return 1;
};

const pickAudio = (report) =>
  !report.kind || report.kind === 'audio' || report.mediaType === 'audio';

/**
 * Flatten one RTCStatsReport into the raw fields we care about.
 * Only audio is considered — this is a voice product.
 */
const readReport = (statsReport) => {
  const out = {
    inbound: null,
    outbound: null,
    remoteInbound: null,
    remoteOutbound: null,
    mediaSource: null,
    candidatePair: null,
    localCandidate: null,
    remoteCandidate: null,
    transport: null,
    inboundCodec: null,
    outboundCodec: null,
    _codecs: new Map(),
    _candidates: new Map(),
  };

  statsReport.forEach((r) => {
    switch (r.type) {
      case 'codec':
        out._codecs.set(r.id, r);
        break;
      case 'local-candidate':
      case 'remote-candidate':
        out._candidates.set(r.id, r);
        break;
      case 'inbound-rtp':
        if (pickAudio(r)) out.inbound = r;
        break;
      case 'outbound-rtp':
        if (pickAudio(r)) out.outbound = r;
        break;
      case 'remote-inbound-rtp':
        if (pickAudio(r)) out.remoteInbound = r;
        break;
      case 'remote-outbound-rtp':
        if (pickAudio(r)) out.remoteOutbound = r;
        break;
      case 'media-source':
        if (pickAudio(r)) out.mediaSource = r;
        break;
      case 'transport':
        out.transport = r;
        break;
      case 'candidate-pair':
        // Chrome marks the live pair with `selected`; spec uses the transport's
        // selectedCandidatePairId. Prefer an explicitly selected/succeeded pair.
        if (r.selected === true) {
          out.candidatePair = r;
        } else if (!out.candidatePair && r.state === 'succeeded') {
          out.candidatePair = r;
        }
        break;
      default:
        break;
    }
  });

  if (out.transport?.selectedCandidatePairId) {
    statsReport.forEach((r) => {
      if (r.id === out.transport.selectedCandidatePairId) out.candidatePair = r;
    });
  }
  if (out.candidatePair) {
    out.localCandidate =
      out._candidates.get(out.candidatePair.localCandidateId) || null;
    out.remoteCandidate =
      out._candidates.get(out.candidatePair.remoteCandidateId) || null;
  }
  if (out.inbound?.codecId) {
    out.inboundCodec = out._codecs.get(out.inbound.codecId) || null;
  }
  if (out.outbound?.codecId) {
    out.outboundCodec = out._codecs.get(out.outbound.codecId) || null;
  }

  delete out._codecs;
  delete out._candidates;
  return out;
};

const describeCodec = (codec) => {
  if (!codec) return null;
  const mime = codec.mimeType || '';
  const clockRate = num(codec.clockRate);
  return {
    mimeType: mime || null,
    name: mime.includes('/') ? mime.split('/')[1] : mime || null,
    clockRate,
    payloadType: num(codec.payloadType),
    channels: num(codec.channels),
    // Verifies what the SBC actually negotiated: HD voice or narrowband
    // fallback, plus Opus params (useinbandfec / usedtx / maxaveragebitrate).
    isWideband:
      clockRate === null ? null : clockRate >= QUALITY_THRESHOLDS.wideBandClockRate,
    fmtp: codec.sdpFmtpLine || null,
  };
};

/**
 * Speech-energy ratio for one leg. totalAudioEnergy is the integral of squared
 * sample amplitude; dividing the window's energy by its duration gives mean
 * power, which tracks "was somebody actually talking" far better than a single
 * audioLevel snapshot taken every 2s.
 */
const speechRatio = (energyDelta, durationDelta) => {
  if (energyDelta === null || !durationDelta) return null;
  // sqrt(mean power) ~ RMS amplitude in 0..1
  return clamp(Math.sqrt(energyDelta / durationDelta), 0, 1);
};

/** Build one derived sample from the current + previous raw readings. */
const buildSample = (curr, prev, elapsedMs) => {
  const seconds = elapsedMs > 0 ? elapsedMs / 1000 : null;

  // ---- Inbound (what the agent hears) -------------------------------------
  const inRecvDelta = safeDelta(curr.inbound?.packetsReceived, prev?.inbound?.packetsReceived);
  const inLostDelta = safeDelta(curr.inbound?.packetsLost, prev?.inbound?.packetsLost);
  const inBytesDelta = safeDelta(curr.inbound?.bytesReceived, prev?.inbound?.bytesReceived);
  const inHeaderBytesDelta = safeDelta(
    curr.inbound?.headerBytesReceived,
    prev?.inbound?.headerBytesReceived
  );
  const inExpected =
    inRecvDelta !== null && inLostDelta !== null ? inRecvDelta + inLostDelta : null;
  const inboundLossPercent =
    inExpected && inExpected > 0 ? (inLostDelta / inExpected) * 100 : inExpected === 0 ? 0 : null;

  // jitterBufferDelay is cumulative seconds; divide by emitted count for the
  // per-packet playout delay actually experienced in this window.
  const jbDelayDelta = safeDelta(
    curr.inbound?.jitterBufferDelay,
    prev?.inbound?.jitterBufferDelay
  );
  const jbCountDelta = safeDelta(
    curr.inbound?.jitterBufferEmittedCount,
    prev?.inbound?.jitterBufferEmittedCount
  );
  const jitterBufferDelayMs =
    jbDelayDelta !== null && jbCountDelta ? (jbDelayDelta / jbCountDelta) * 1000 : null;
  const jbTargetDelta = safeDelta(
    curr.inbound?.jitterBufferTargetDelay,
    prev?.inbound?.jitterBufferTargetDelay
  );
  const jitterBufferTargetMs =
    jbTargetDelta !== null && jbCountDelta ? (jbTargetDelta / jbCountDelta) * 1000 : null;

  // Concealment = samples the decoder had to synthesise (PLC) => audible gaps.
  // silentConcealedSamples are concealment during silence, which nobody hears,
  // so the audible figure is the honest one.
  const concealedDelta = safeDelta(
    curr.inbound?.concealedSamples,
    prev?.inbound?.concealedSamples
  );
  const silentConcealedDelta = safeDelta(
    curr.inbound?.silentConcealedSamples,
    prev?.inbound?.silentConcealedSamples
  );
  const receivedSamplesDelta = safeDelta(
    curr.inbound?.totalSamplesReceived,
    prev?.inbound?.totalSamplesReceived
  );
  const concealmentPercent =
    concealedDelta !== null && receivedSamplesDelta
      ? (concealedDelta / receivedSamplesDelta) * 100
      : null;
  const audibleConcealmentPercent =
    concealedDelta !== null && receivedSamplesDelta
      ? (Math.max(0, concealedDelta - (silentConcealedDelta ?? 0)) /
          receivedSamplesDelta) *
        100
      : null;

  // Jitter buffer stretching/compressing audio to track a drifting clock.
  // High values = pitch artefacts users describe as "robotic".
  const insertedDelta = safeDelta(
    curr.inbound?.insertedSamplesForDeceleration,
    prev?.inbound?.insertedSamplesForDeceleration
  );
  const removedDelta = safeDelta(
    curr.inbound?.removedSamplesForAcceleration,
    prev?.inbound?.removedSamplesForAcceleration
  );
  const bufferAdjustPercent =
    (insertedDelta !== null || removedDelta !== null) && receivedSamplesDelta
      ? (((insertedDelta ?? 0) + (removedDelta ?? 0)) / receivedSamplesDelta) * 100
      : null;

  const inEnergyDelta = safeDelta(
    curr.inbound?.totalAudioEnergy,
    prev?.inbound?.totalAudioEnergy
  );
  const inDurationDelta = safeDelta(
    curr.inbound?.totalSamplesDuration,
    prev?.inbound?.totalSamplesDuration
  );

  // ---- Outbound (what the customer hears) ---------------------------------
  const outSentDelta = safeDelta(curr.outbound?.packetsSent, prev?.outbound?.packetsSent);
  const outBytesDelta = safeDelta(curr.outbound?.bytesSent, prev?.outbound?.bytesSent);
  const outHeaderBytesDelta = safeDelta(
    curr.outbound?.headerBytesSent,
    prev?.outbound?.headerBytesSent
  );
  const remoteLostTotal = num(curr.remoteInbound?.packetsLost);
  const remoteLostDelta = safeDelta(
    curr.remoteInbound?.packetsLost,
    prev?.remoteInbound?.packetsLost
  );
  const outboundLossPercent =
    remoteLostDelta !== null && outSentDelta
      ? (remoteLostDelta / (outSentDelta + remoteLostDelta)) * 100
      : num(curr.remoteInbound?.fractionLost) !== null
        ? num(curr.remoteInbound.fractionLost) * 100
        : null;

  // Mean time each packet waited in the send queue. Rises BEFORE loss does
  // when the agent's uplink is congested — earliest available warning.
  const sendDelayDelta = safeDelta(
    curr.outbound?.totalPacketSendDelay,
    prev?.outbound?.totalPacketSendDelay
  );
  const packetSendDelayMs =
    sendDelayDelta !== null && outSentDelta ? (sendDelayDelta / outSentDelta) * 1000 : null;

  // ---- RTT: prefer RTCP-reported, fall back to ICE candidate pair ---------
  const rtcpRttMs =
    num(curr.remoteInbound?.roundTripTime) !== null
      ? curr.remoteInbound.roundTripTime * 1000
      : null;
  const iceRttMs =
    num(curr.candidatePair?.currentRoundTripTime) !== null
      ? curr.candidatePair.currentRoundTripTime * 1000
      : null;
  const rttMs = rtcpRttMs ?? iceRttMs;
  // Unbiased whole-call average straight from the browser's own accumulator.
  const rttMeasurements = num(curr.remoteInbound?.roundTripTimeMeasurements);
  const rttAvgMs =
    num(curr.remoteInbound?.totalRoundTripTime) !== null && rttMeasurements
      ? (curr.remoteInbound.totalRoundTripTime / rttMeasurements) * 1000
      : null;

  const inboundJitterMs =
    num(curr.inbound?.jitter) !== null ? curr.inbound.jitter * 1000 : null;
  const outboundJitterMs =
    num(curr.remoteInbound?.jitter) !== null ? curr.remoteInbound.jitter * 1000 : null;

  const inboundBitrateKbps =
    inBytesDelta !== null && seconds ? (inBytesDelta * 8) / 1000 / seconds : null;
  const outboundBitrateKbps =
    outBytesDelta !== null && seconds ? (outBytesDelta * 8) / 1000 / seconds : null;

  // ---- Mic / capture side -------------------------------------------------
  const micEnergyDelta = safeDelta(
    curr.mediaSource?.totalAudioEnergy,
    prev?.mediaSource?.totalAudioEnergy
  );
  const micDurationDelta = safeDelta(
    curr.mediaSource?.totalSamplesDuration,
    prev?.mediaSource?.totalSamplesDuration
  );
  const droppedSamplesDelta = safeDelta(
    curr.mediaSource?.droppedSamplesDuration,
    prev?.mediaSource?.droppedSamplesDuration
  );
  const droppedEventsDelta = safeDelta(
    curr.mediaSource?.droppedSamplesEvents,
    prev?.mediaSource?.droppedSamplesEvents
  );
  const captureDelayDelta = safeDelta(
    curr.mediaSource?.totalCaptureDelay,
    prev?.mediaSource?.totalCaptureDelay
  );
  const capturedSamplesDelta = safeDelta(
    curr.mediaSource?.totalSamplesCaptured,
    prev?.mediaSource?.totalSamplesCaptured
  );
  const captureDelayMs =
    captureDelayDelta !== null && capturedSamplesDelta
      ? (captureDelayDelta / capturedSamplesDelta) * 1000
      : null;

  // MOS is scored on the worse of the two directions so a bad uplink is not
  // hidden by a clean downlink.
  const inboundMos = estimateMos({
    rttMs,
    jitterMs: inboundJitterMs,
    lossPercent: inboundLossPercent,
  });
  const outboundMos = estimateMos({
    rttMs,
    jitterMs: outboundJitterMs,
    lossPercent: outboundLossPercent,
  });
  const mos = Math.min(inboundMos, outboundMos);

  const micLevel = num(curr.mediaSource?.audioLevel);
  const remoteLevel = num(curr.inbound?.audioLevel);

  return {
    at: new Date().toISOString(),
    intervalMs: elapsedMs,

    mos: round(mos),
    inboundMos: round(inboundMos),
    outboundMos: round(outboundMos),
    label: mosToLabel(mos),
    bars: mosToBars(mos),

    rttMs: round(rttMs, 1),
    rttAvgMs: round(rttAvgMs, 1),
    rttSource: rtcpRttMs !== null ? 'rtcp' : iceRttMs !== null ? 'ice' : null,
    // Zero RTCP reports means our whole "what the customer hears" view is
    // blind — worth knowing before trusting outbound numbers.
    rtcpReportsReceived: num(curr.remoteInbound?.reportsReceived),

    inbound: {
      lossPercent: round(inboundLossPercent),
      jitterMs: round(inboundJitterMs, 1),
      bitrateKbps: round(inboundBitrateKbps, 1),
      packets: inRecvDelta,
      packetsLost: inLostDelta,
      totalPacketsReceived: num(curr.inbound?.packetsReceived),
      totalPacketsLost: num(curr.inbound?.packetsLost),
      jitterBufferDelayMs: round(jitterBufferDelayMs, 1),
      jitterBufferTargetMs: round(jitterBufferTargetMs, 1),
      concealmentPercent: round(concealmentPercent),
      audibleConcealmentPercent: round(audibleConcealmentPercent),
      bufferAdjustPercent: round(bufferAdjustPercent),
      packetsDiscarded: safeDelta(
        curr.inbound?.packetsDiscarded,
        prev?.inbound?.packetsDiscarded
      ),
      fecPacketsReceived: safeDelta(
        curr.inbound?.fecPacketsReceived,
        prev?.inbound?.fecPacketsReceived
      ),
      retransmittedPackets: safeDelta(
        curr.inbound?.retransmittedPacketsReceived,
        prev?.inbound?.retransmittedPacketsReceived
      ),
      headerOverheadPercent:
        inHeaderBytesDelta !== null && inBytesDelta
          ? round((inHeaderBytesDelta / inBytesDelta) * 100, 1)
          : null,
      audioLevel: round(remoteLevel, 4),
      speechRatio: round(speechRatio(inEnergyDelta, inDurationDelta), 4),
      lastPacketReceivedAt: num(curr.inbound?.lastPacketReceivedTimestamp),
      codec: describeCodec(curr.inboundCodec),
    },

    outbound: {
      lossPercent: round(outboundLossPercent),
      jitterMs: round(outboundJitterMs, 1),
      bitrateKbps: round(outboundBitrateKbps, 1),
      targetBitrateKbps:
        num(curr.outbound?.targetBitrate) !== null
          ? round(curr.outbound.targetBitrate / 1000, 1)
          : null,
      packets: outSentDelta,
      totalPacketsSent: num(curr.outbound?.packetsSent),
      remotePacketsLost: remoteLostTotal,
      retransmittedPackets: num(curr.outbound?.retransmittedPacketsSent),
      nackCount: num(curr.outbound?.nackCount),
      packetSendDelayMs: round(packetSendDelayMs, 1),
      headerOverheadPercent:
        outHeaderBytesDelta !== null && outBytesDelta
          ? round((outHeaderBytesDelta / outBytesDelta) * 100, 1)
          : null,
      micAudioLevel: round(micLevel, 4),
      micSpeechRatio: round(speechRatio(micEnergyDelta, micDurationDelta), 4),
      codec: describeCodec(curr.outboundCodec),
    },

    mic: {
      // Echo return loss: how much of the speaker signal the canceller removes
      // before it re-enters the mic. Low = customer hears themselves.
      echoReturnLossDb: round(num(curr.mediaSource?.echoReturnLoss), 1),
      echoReturnLossEnhancementDb: round(
        num(curr.mediaSource?.echoReturnLossEnhancement),
        1
      ),
      // Capture-side loss: mic driver / CPU starvation on the agent's machine,
      // NOT the network. Separates "bad PC" from "bad line".
      droppedSamplesSeconds: round(droppedSamplesDelta, 3),
      droppedSamplesEvents: droppedEventsDelta,
      captureDelayMs: round(captureDelayMs, 1),
    },

    transport: {
      availableOutgoingBitrateKbps:
        num(curr.candidatePair?.availableOutgoingBitrate) !== null
          ? round(curr.candidatePair.availableOutgoingBitrate / 1000, 1)
          : null,
      availableIncomingBitrateKbps:
        num(curr.candidatePair?.availableIncomingBitrate) !== null
          ? round(curr.candidatePair.availableIncomingBitrate / 1000, 1)
          : null,
      localCandidateType: curr.localCandidate?.candidateType || null,
      remoteCandidateType: curr.remoteCandidate?.candidateType || null,
      protocol: curr.candidatePair?.protocol || curr.localCandidate?.protocol || null,
      networkType: curr.localCandidate?.networkType || null,
      relayUsed:
        curr.localCandidate?.candidateType === 'relay' ||
        curr.remoteCandidate?.candidateType === 'relay',
      // Which STUN/TURN server, and over what (udp/tcp/tls). TCP/TLS relay
      // explains an otherwise unexplained latency floor.
      turnServer: curr.localCandidate?.url || null,
      relayProtocol: curr.localCandidate?.relayProtocol || null,
      // Non-zero after the first change = agent's network path moved mid-call
      // (WiFi -> LTE, AP handoff). Explains a sudden one-off glitch.
      pathChanges: num(curr.transport?.selectedCandidatePairChanges),
      consentRequestsSent: num(curr.candidatePair?.consentRequestsSent),
      stunResponsesReceived: num(curr.candidatePair?.responsesReceived),
      dtlsState: curr.transport?.dtlsState || null,
      iceState: curr.transport?.iceState || curr.candidatePair?.state || null,
      // Encryption audit trail: proves SRTP is really on and with what cipher.
      srtpCipher: curr.transport?.srtpCipher || null,
      dtlsCipher: curr.transport?.dtlsCipher || null,
      tlsVersion: curr.transport?.tlsVersion || null,
      dtlsRole: curr.transport?.dtlsRole || null,
    },
  };
};

const buildWarnings = (sample, streak) => {
  const w = [];
  const T = QUALITY_THRESHOLDS;
  const inb = sample.inbound;
  const outb = sample.outbound;
  const mic = sample.mic;

  if (inb.lossPercent !== null && inb.lossPercent >= T.lossBad) {
    w.push({ code: 'inbound_loss_high', severity: 'critical', message: `Heavy packet loss on incoming audio (${inb.lossPercent}%)` });
  } else if (inb.lossPercent !== null && inb.lossPercent >= T.lossWarn) {
    w.push({ code: 'inbound_loss', severity: 'warning', message: `Packet loss on incoming audio (${inb.lossPercent}%)` });
  }

  if (outb.lossPercent !== null && outb.lossPercent >= T.lossBad) {
    w.push({ code: 'outbound_loss_high', severity: 'critical', message: `Customer is losing your audio (${outb.lossPercent}% loss)` });
  } else if (outb.lossPercent !== null && outb.lossPercent >= T.lossWarn) {
    w.push({ code: 'outbound_loss', severity: 'warning', message: `Loss on your outgoing audio (${outb.lossPercent}%)` });
  }

  const worstJitter = Math.max(inb.jitterMs ?? 0, outb.jitterMs ?? 0);
  if (worstJitter >= T.jitterBad) {
    w.push({ code: 'jitter_high', severity: 'critical', message: `Unstable network — jitter ${Math.round(worstJitter)}ms` });
  } else if (worstJitter >= T.jitterWarn) {
    w.push({ code: 'jitter', severity: 'warning', message: `Network jitter ${Math.round(worstJitter)}ms` });
  }

  if (sample.rttMs !== null && sample.rttMs >= T.rttBad) {
    w.push({ code: 'rtt_high', severity: 'critical', message: `Very high latency (${Math.round(sample.rttMs)}ms round trip)` });
  } else if (sample.rttMs !== null && sample.rttMs >= T.rttWarn) {
    w.push({ code: 'rtt', severity: 'warning', message: `High latency (${Math.round(sample.rttMs)}ms round trip)` });
  }

  // Prefer the audible figure; fall back to total when the browser does not
  // report silentConcealedSamples.
  const conceal = inb.audibleConcealmentPercent ?? inb.concealmentPercent;
  if (conceal !== null && conceal >= T.concealmentBad) {
    w.push({ code: 'audio_gaps_high', severity: 'critical', message: `Choppy incoming audio (${conceal}% concealed)` });
  } else if (conceal !== null && conceal >= T.concealmentWarn) {
    w.push({ code: 'audio_gaps', severity: 'warning', message: `Some choppy audio (${conceal}% concealed)` });
  }

  if (mic.echoReturnLossDb !== null && mic.echoReturnLossDb < T.erlBad) {
    w.push({ code: 'echo_high', severity: 'critical', message: `Echo likely — customer may hear themselves (ERL ${mic.echoReturnLossDb}dB)` });
  } else if (mic.echoReturnLossDb !== null && mic.echoReturnLossDb < T.erlWarn) {
    w.push({ code: 'echo', severity: 'warning', message: `Weak echo cancellation (ERL ${mic.echoReturnLossDb}dB)` });
  }

  if (mic.droppedSamplesSeconds !== null && mic.droppedSamplesSeconds >= T.captureDropWarn) {
    w.push({ code: 'mic_capture_drops', severity: 'warning', message: `Microphone dropping audio (${mic.droppedSamplesSeconds}s lost) — check headset / PC load` });
  }

  if (outb.packetSendDelayMs !== null && outb.packetSendDelayMs >= T.sendDelayBad) {
    w.push({ code: 'uplink_congested_high', severity: 'critical', message: `Your upload is congested (${outb.packetSendDelayMs}ms send delay)` });
  } else if (outb.packetSendDelayMs !== null && outb.packetSendDelayMs >= T.sendDelayWarn) {
    w.push({ code: 'uplink_congested', severity: 'warning', message: `Upload queueing detected (${outb.packetSendDelayMs}ms send delay)` });
  }

  if (inb.bufferAdjustPercent !== null && inb.bufferAdjustPercent >= T.bufferAdjustWarn) {
    w.push({ code: 'clock_drift', severity: 'warning', message: `Audio being stretched to track clock drift (${inb.bufferAdjustPercent}%) — may sound robotic` });
  }

  if (streak.pathChanged) {
    w.push({ code: 'network_path_changed', severity: 'warning', message: 'Network path changed mid-call (WiFi/LTE switch)' });
  }

  const clockRate = inb.codec?.clockRate ?? outb.codec?.clockRate;
  if (clockRate !== null && clockRate !== undefined && clockRate < T.wideBandClockRate) {
    w.push({ code: 'narrowband_codec', severity: 'info', message: `Narrowband codec in use (${inb.codec?.name || outb.codec?.name || '?'} ${clockRate}Hz) — no HD voice` });
  }

  if (sample.rtcpReportsReceived === 0) {
    w.push({ code: 'no_rtcp_reports', severity: 'info', message: 'No RTCP reports from far end — outgoing quality cannot be measured' });
  }

  if (streak.silentMic >= T.silentSamplesForAlert) {
    w.push({ code: 'mic_silent', severity: 'critical', message: 'No audio detected from your microphone' });
  }
  if (streak.silentRemote >= T.silentSamplesForAlert) {
    w.push({ code: 'remote_silent', severity: 'warning', message: 'No audio detected from the other party' });
  }
  if (streak.noInboundPackets >= 2) {
    w.push({ code: 'no_inbound_media', severity: 'critical', message: 'Not receiving audio packets (one-way audio)' });
  }
  if (streak.noOutboundPackets >= 2) {
    w.push({ code: 'no_outbound_media', severity: 'critical', message: 'Not sending audio packets (one-way audio)' });
  }
  if (sample.transport.relayUsed) {
    w.push({
      code: 'relay',
      severity: 'info',
      message: `Media is going through a TURN relay${
        sample.transport.relayProtocol ? ` (${sample.transport.relayProtocol})` : ''
      }`,
    });
  }
  return w;
};

const makeAccumulator = () => ({
  samples: 0,
  mosSum: 0,
  mosMin: null,
  mosMax: null,
  inLossSum: 0,
  inLossCount: 0,
  inLossMax: 0,
  outLossSum: 0,
  outLossCount: 0,
  outLossMax: 0,
  jitterSum: 0,
  jitterCount: 0,
  jitterMax: 0,
  rttSum: 0,
  rttCount: 0,
  rttMax: 0,
  concealSum: 0,
  concealCount: 0,
  audibleConcealSum: 0,
  audibleConcealCount: 0,
  bufferAdjustSum: 0,
  bufferAdjustCount: 0,
  inBitrateSum: 0,
  inBitrateCount: 0,
  outBitrateSum: 0,
  outBitrateCount: 0,
  sendDelaySum: 0,
  sendDelayCount: 0,
  sendDelayMax: 0,
  erlSum: 0,
  erlCount: 0,
  erlMin: null,
  micSpeechSum: 0,
  micSpeechCount: 0,
  remoteSpeechSum: 0,
  remoteSpeechCount: 0,
  micDroppedSeconds: 0,
  micDroppedEvents: 0,
  badSamples: 0,
  warningCodes: new Set(),
});

const accumulate = (acc, sample, warnings) => {
  acc.samples += 1;
  if (sample.mos !== null) {
    acc.mosSum += sample.mos;
    acc.mosMin = acc.mosMin === null ? sample.mos : Math.min(acc.mosMin, sample.mos);
    acc.mosMax = acc.mosMax === null ? sample.mos : Math.max(acc.mosMax, sample.mos);
    if (sample.mos < 3.1) acc.badSamples += 1;
  }
  const addAvg = (key, value) => {
    if (value === null || value === undefined) return;
    acc[`${key}Sum`] += value;
    acc[`${key}Count`] += 1;
    const maxKey = `${key}Max`;
    if (maxKey in acc) acc[maxKey] = Math.max(acc[maxKey], value);
    const minKey = `${key}Min`;
    if (minKey in acc) {
      acc[minKey] = acc[minKey] === null ? value : Math.min(acc[minKey], value);
    }
  };
  addAvg('inLoss', sample.inbound.lossPercent);
  addAvg('outLoss', sample.outbound.lossPercent);
  // Track the worse direction, but only when at least one direction reported.
  const inJ = sample.inbound.jitterMs;
  const outJ = sample.outbound.jitterMs;
  addAvg(
    'jitter',
    inJ === null && outJ === null ? null : Math.max(inJ ?? 0, outJ ?? 0)
  );
  addAvg('rtt', sample.rttMs);
  addAvg('conceal', sample.inbound.concealmentPercent);
  addAvg('audibleConceal', sample.inbound.audibleConcealmentPercent);
  addAvg('bufferAdjust', sample.inbound.bufferAdjustPercent);
  addAvg('inBitrate', sample.inbound.bitrateKbps);
  addAvg('outBitrate', sample.outbound.bitrateKbps);
  addAvg('sendDelay', sample.outbound.packetSendDelayMs);
  addAvg('erl', sample.mic.echoReturnLossDb);
  addAvg('micSpeech', sample.outbound.micSpeechRatio);
  addAvg('remoteSpeech', sample.inbound.speechRatio);
  if (sample.mic.droppedSamplesSeconds) {
    acc.micDroppedSeconds += sample.mic.droppedSamplesSeconds;
  }
  if (sample.mic.droppedSamplesEvents) {
    acc.micDroppedEvents += sample.mic.droppedSamplesEvents;
  }
  warnings.forEach((x) => acc.warningCodes.add(x.code));
};

/**
 * Downsample the sample list into a compact timeline for storage / charting.
 * Only the fields a graph needs, so the JSON stays small.
 */
const buildTimeline = (samples) => {
  if (!samples.length) return [];
  const step = Math.max(1, Math.ceil(samples.length / MAX_TIMELINE_POINTS));
  const out = [];
  for (let i = 0; i < samples.length; i += step) {
    const s = samples[i];
    out.push({
      at: s.at,
      mos: s.mos,
      in_loss: s.inbound.lossPercent,
      out_loss: s.outbound.lossPercent,
      in_jitter: s.inbound.jitterMs,
      out_jitter: s.outbound.jitterMs,
      rtt: s.rttMs,
      in_kbps: s.inbound.bitrateKbps,
      out_kbps: s.outbound.bitrateKbps,
      conceal: s.inbound.audibleConcealmentPercent ?? s.inbound.concealmentPercent,
      send_delay: s.outbound.packetSendDelayMs,
      erl: s.mic.echoReturnLossDb,
      mic_speech: s.outbound.micSpeechRatio,
      remote_speech: s.inbound.speechRatio,
    });
  }
  return out;
};

const summarise = (acc, meta, samples) => {
  const avg = (sum, count, d = 2) =>
    count > 0 ? Math.round((sum / count) * 10 ** d) / 10 ** d : null;
  const avgMos = avg(acc.mosSum, acc.samples);
  const micSpeechAvg = avg(acc.micSpeechSum, acc.micSpeechCount, 4);
  const remoteSpeechAvg = avg(acc.remoteSpeechSum, acc.remoteSpeechCount, 4);
  const talkTotal = (micSpeechAvg ?? 0) + (remoteSpeechAvg ?? 0);

  return {
    call_sid: meta.callSid || null,
    direction: meta.direction || null,
    started_at: meta.startedAt || null,
    ended_at: new Date().toISOString(),
    duration_seconds: meta.startedAt
      ? Math.max(0, Math.round((Date.now() - new Date(meta.startedAt).getTime()) / 1000))
      : null,
    samples: acc.samples,

    mos_avg: avgMos,
    mos_min: acc.mosMin,
    mos_max: acc.mosMax,
    quality_label: mosToLabel(avgMos),
    bad_sample_percent:
      acc.samples > 0 ? Math.round((acc.badSamples / acc.samples) * 1000) / 10 : null,

    inbound_loss_avg: avg(acc.inLossSum, acc.inLossCount),
    inbound_loss_max: acc.inLossCount ? round(acc.inLossMax) : null,
    outbound_loss_avg: avg(acc.outLossSum, acc.outLossCount),
    outbound_loss_max: acc.outLossCount ? round(acc.outLossMax) : null,
    jitter_avg_ms: avg(acc.jitterSum, acc.jitterCount, 1),
    jitter_max_ms: acc.jitterCount ? round(acc.jitterMax, 1) : null,
    rtt_avg_ms: avg(acc.rttSum, acc.rttCount, 1),
    rtt_max_ms: acc.rttCount ? round(acc.rttMax, 1) : null,

    concealment_avg_percent: avg(acc.concealSum, acc.concealCount),
    audible_concealment_avg_percent: avg(acc.audibleConcealSum, acc.audibleConcealCount),
    buffer_adjust_avg_percent: avg(acc.bufferAdjustSum, acc.bufferAdjustCount),

    inbound_bitrate_avg_kbps: avg(acc.inBitrateSum, acc.inBitrateCount, 1),
    outbound_bitrate_avg_kbps: avg(acc.outBitrateSum, acc.outBitrateCount, 1),
    send_delay_avg_ms: avg(acc.sendDelaySum, acc.sendDelayCount, 1),
    send_delay_max_ms: acc.sendDelayCount ? round(acc.sendDelayMax, 1) : null,

    echo_return_loss_avg_db: avg(acc.erlSum, acc.erlCount, 1),
    echo_return_loss_min_db: acc.erlMin === null ? null : round(acc.erlMin, 1),
    mic_dropped_seconds: round(acc.micDroppedSeconds, 3),
    mic_dropped_events: acc.micDroppedEvents || 0,

    // Rough talk-share between the two legs, from speech energy only (no ASR).
    agent_speech_ratio: micSpeechAvg,
    customer_speech_ratio: remoteSpeechAvg,
    agent_talk_share_percent:
      talkTotal > 0 ? Math.round(((micSpeechAvg ?? 0) / talkTotal) * 1000) / 10 : null,

    audio_start_latency_ms: meta.audioStartLatencyMs ?? null,

    inbound_codec: meta.inboundCodec || null,
    outbound_codec: meta.outboundCodec || null,
    codec_clock_rate: meta.codecClockRate ?? null,
    is_wideband: meta.isWideband ?? null,
    network_type: meta.networkType || null,
    candidate_type: meta.candidateType || null,
    remote_candidate_type: meta.remoteCandidateType || null,
    protocol: meta.protocol || null,
    relay_used: Boolean(meta.relayUsed),
    turn_server: meta.turnServer || null,
    relay_protocol: meta.relayProtocol || null,
    network_path_changes: meta.pathChanges ?? null,
    srtp_cipher: meta.srtpCipher || null,
    dtls_cipher: meta.dtlsCipher || null,
    tls_version: meta.tlsVersion || null,

    device: meta.device || null,
    warnings: Array.from(acc.warningCodes),
    timeline: buildTimeline(samples),
  };
};

/**
 * Mic track settings actually applied by the browser — tells you which headset
 * the agent used and whether the DSP features were really enabled. Read once
 * on start; these do not change mid-call.
 */
const readDeviceInfo = async (pc) => {
  try {
    const sender = (pc.getSenders?.() || []).find((s) => s.track?.kind === 'audio');
    const track = sender?.track;
    if (!track) return null;
    const settings = typeof track.getSettings === 'function' ? track.getSettings() : {};
    let label = track.label || null;
    if (!label && settings.deviceId && navigator.mediaDevices?.enumerateDevices) {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        label = devices.find((d) => d.deviceId === settings.deviceId)?.label || null;
      } catch (e) {}
    }
    return {
      mic_label: label,
      sample_rate: num(settings.sampleRate),
      channel_count: num(settings.channelCount),
      echo_cancellation: settings.echoCancellation ?? null,
      noise_suppression: settings.noiseSuppression ?? null,
      auto_gain_control: settings.autoGainControl ?? null,
      latency: num(settings.latency),
      user_agent:
        typeof navigator !== 'undefined' ? navigator.userAgent || null : null,
      // Coarse connection hint where available (Chromium only).
      connection_type:
        typeof navigator !== 'undefined' && navigator.connection
          ? navigator.connection.effectiveType || null
          : null,
    };
  } catch (e) {
    return null;
  }
};

/**
 * Start polling a peer connection.
 *
 * @param {object}   opts
 * @param {Function} opts.getPeerConnection  () => RTCPeerConnection | null  (re-read every tick; survives re-INVITE)
 * @param {string}   [opts.callSid]
 * @param {string}   [opts.direction]
 * @param {number}   [opts.intervalMs]
 * @param {Function} [opts.onSample]  (sample, warnings) => void
 * @param {Function} [opts.onStop]    (summary) => void
 * @returns {{stop: Function, getSummary: Function, getSamples: Function, getLastSample: Function, isRunning: Function, callSid: string|null}}
 */
export const startWebrtcStatsMonitor = ({
  getPeerConnection,
  callSid = null,
  direction = null,
  intervalMs = DEFAULT_INTERVAL_MS,
  onSample,
  onStop,
} = {}) => {
  const startedAtMs = Date.now();
  const startedAt = new Date(startedAtMs).toISOString();
  const acc = makeAccumulator();
  const samples = [];
  const streak = {
    silentMic: 0,
    silentRemote: 0,
    noInboundPackets: 0,
    noOutboundPackets: 0,
    pathChanged: false,
  };
  const meta: any = { callSid, direction, startedAt };

  let prevRaw = null;
  let prevAt = null;
  let lastSample = null;
  let timer = null;
  let stopped = false;
  let summary = null;
  let inFlight = false;
  let deviceRead = false;
  let firstPathChanges = null;

  const tick = async () => {
    if (stopped || inFlight) return;
    inFlight = true;
    try {
      const pc = typeof getPeerConnection === 'function' ? getPeerConnection() : null;
      if (!pc || typeof pc.getStats !== 'function') return;
      // A closed PC still answers getStats() with stale counters — skip it.
      if (pc.connectionState === 'closed' || pc.signalingState === 'closed') return;

      if (!deviceRead) {
        deviceRead = true;
        readDeviceInfo(pc).then((info) => {
          if (info) meta.device = info;
        });
      }

      const report = await pc.getStats();
      // stop() may have run while getStats() was in flight — don't publish a
      // sample for a call that already ended.
      if (stopped) return;
      const raw = readReport(report);
      const now = Date.now();
      const elapsed = prevAt ? now - prevAt : intervalMs;

      // Time from monitor start until the first inbound audio packet — the
      // "customer answered but heard nothing for 3s" metric.
      if (
        meta.audioStartLatencyMs === undefined &&
        num(raw.inbound?.packetsReceived) > 0
      ) {
        meta.audioStartLatencyMs = now - startedAtMs;
      }

      // First tick establishes the counter baseline only; deltas need two reads.
      if (!prevRaw) {
        prevRaw = raw;
        prevAt = now;
        return;
      }

      const sample = buildSample(raw, prevRaw, elapsed);
      prevRaw = raw;
      prevAt = now;

      const micLevel = sample.outbound.micAudioLevel;
      const remoteLevel = sample.inbound.audioLevel;
      streak.silentMic =
        micLevel !== null && micLevel < QUALITY_THRESHOLDS.silenceLevel
          ? streak.silentMic + 1
          : 0;
      streak.silentRemote =
        remoteLevel !== null && remoteLevel < QUALITY_THRESHOLDS.silenceLevel
          ? streak.silentRemote + 1
          : 0;
      streak.noInboundPackets = sample.inbound.packets === 0 ? streak.noInboundPackets + 1 : 0;
      streak.noOutboundPackets = sample.outbound.packets === 0 ? streak.noOutboundPackets + 1 : 0;

      // selectedCandidatePairChanges is 1 for a normal call; only growth past
      // the first observed value means the media path actually moved.
      const pathChanges = sample.transport.pathChanges;
      if (pathChanges !== null) {
        if (firstPathChanges === null) firstPathChanges = pathChanges;
        else if (pathChanges > firstPathChanges) streak.pathChanged = true;
      }

      const warnings = buildWarnings(sample, streak);
      sample.warnings = warnings;

      meta.inboundCodec = sample.inbound.codec?.name || meta.inboundCodec || null;
      meta.outboundCodec = sample.outbound.codec?.name || meta.outboundCodec || null;
      meta.codecClockRate =
        sample.inbound.codec?.clockRate ??
        sample.outbound.codec?.clockRate ??
        meta.codecClockRate ??
        null;
      meta.isWideband =
        sample.inbound.codec?.isWideband ??
        sample.outbound.codec?.isWideband ??
        meta.isWideband ??
        null;
      meta.networkType = sample.transport.networkType || meta.networkType || null;
      meta.candidateType = sample.transport.localCandidateType || meta.candidateType || null;
      meta.remoteCandidateType =
        sample.transport.remoteCandidateType || meta.remoteCandidateType || null;
      meta.protocol = sample.transport.protocol || meta.protocol || null;
      meta.relayUsed = meta.relayUsed || sample.transport.relayUsed;
      meta.turnServer = sample.transport.turnServer || meta.turnServer || null;
      meta.relayProtocol = sample.transport.relayProtocol || meta.relayProtocol || null;
      meta.pathChanges = sample.transport.pathChanges ?? meta.pathChanges ?? null;
      meta.srtpCipher = sample.transport.srtpCipher || meta.srtpCipher || null;
      meta.dtlsCipher = sample.transport.dtlsCipher || meta.dtlsCipher || null;
      meta.tlsVersion = sample.transport.tlsVersion || meta.tlsVersion || null;

      accumulate(acc, sample, warnings);
      lastSample = sample;
      samples.push(sample);
      if (samples.length > MAX_SAMPLES) samples.shift();

      if (typeof onSample === 'function') {
        try {
          onSample(sample, warnings);
        } catch (e) {
          // Never let a consumer break the polling loop.
        }
      }
    } catch (e) {
      // getStats can reject while the PC is tearing down — ignore.
    } finally {
      inFlight = false;
    }
  };

  timer = setInterval(tick, Math.max(500, intervalMs));
  // Prime the baseline immediately so the first real sample lands one tick in.
  tick();

  const stop = () => {
    if (stopped) return summary;
    stopped = true;
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
    summary = summarise(acc, meta, samples);
    if (typeof onStop === 'function') {
      try {
        onStop(summary);
      } catch (e) {}
    }
    return summary;
  };

  return {
    stop,
    callSid,
    isRunning: () => !stopped,
    getLastSample: () => lastSample,
    getSamples: () => samples.slice(),
    getSummary: () => summary || summarise(acc, meta, samples),
  };
};

export default startWebrtcStatsMonitor;
